// v1.9 P0: Badge 原子组件
// 5 variants × 2 sizes
// status: success / warning / error / info / neutral
import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** 让 badge 显示为可点击 (用于 tag/filter) */
  interactive?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<BadgeVariant, string> = {
  success: 'bg-good/10 text-good border-good/30',
  warning: 'bg-warn/10 text-warn border-warn/30',
  error: 'bg-bad/10 text-bad border-bad/30',
  info: 'bg-info/10 text-info border-info/30',
  neutral: 'bg-surface-2 text-ink-2 border-line',
};

const SIZE_CLASS: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'md', interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={twMerge(
        'inline-flex items-center gap-1 font-medium border rounded',
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        interactive && 'cursor-pointer hover:opacity-80 transition-opacity',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
});
